import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import DictationInput from '../DictationInput';
import { toast } from 'sonner';
import { DollarSign, Plus, CheckCircle, Clock, AlertCircle } from 'lucide-react';

export default function RentTracker({ api, houseId, residentId, userId, userName, rentAmount, isStaff }) {
  const [payments, setPayments] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    amount: rentAmount || 0,
    period: new Date().toISOString().slice(0, 7),
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash',
    status: 'paid',
    notes: ''
  });

  const loadPayments = useCallback(() => {
    const query = residentId ? `?resident_id=${residentId}` : '';
    api.get(`/housing/rent-payments${query}`)
      .then(r => setPayments(r.data || []))
      .catch(() => {});
  }, [api, residentId]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const addPayment = async () => {
    if (!form.amount) return toast.error('Amount is required');
    try {
      await api.post('/housing/rent-payments', {
        house_id: houseId,
        resident_id: residentId,
        user_id: userId,
        user_name: userName,
        ...form
      });
      toast.success('Payment recorded!');
      setAddOpen(false);
      setForm(prev => ({ ...prev, notes: '' }));
      loadPayments();
    } catch {
      toast.error('Failed to record payment');
    }
  };

  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);
  const pendingCount = payments.filter(p => p.status === 'pending').length;

  return (
    <Card className="border-0 shadow-sm rounded-xl" data-testid="rent-tracker">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <DollarSign size={14} /> Rent / Fee Tracking
          {payments.length > 0 && <Badge variant="outline" className="text-[10px]">{payments.length} records</Badge>}
        </CardTitle>
        {isStaff && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" data-testid="add-rent-btn">
                <Plus size={12} /> Record Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Record Rent Payment</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Amount ($)</Label>
                    <Input type="number" min={0} step={0.01} value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} data-testid="rent-amount-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Period (Month)</Label>
                    <Input type="month" value={form.period} onChange={e => setForm({...form, period: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Payment Date</Label>
                    <Input type="date" value={form.payment_date} onChange={e => setForm({...form, payment_date: e.target.value})} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Method</Label>
                    <Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="money_order">Money Order</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                        <SelectItem value="voucher">Voucher / Agency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                      <SelectItem value="waived">Waived</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Notes</Label>
                  <DictationInput value={form.notes} onChange={val => setForm({...form, notes: val})} rows={2} placeholder="Optional notes..." showCleanup={false} />
                </div>
                <Button onClick={addPayment} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-rent-payment">Record Payment</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {/* Summary Row */}
        {payments.length > 0 && (
          <div className="flex gap-3 mb-3">
            <div className="flex-1 p-2.5 rounded-lg bg-emerald-50 text-center">
              <p className="text-lg font-bold text-emerald-700">${totalPaid.toFixed(0)}</p>
              <p className="text-[10px] text-emerald-600">Total Paid</p>
            </div>
            <div className="flex-1 p-2.5 rounded-lg bg-amber-50 text-center">
              <p className="text-lg font-bold text-amber-700">{pendingCount}</p>
              <p className="text-[10px] text-amber-600">Pending</p>
            </div>
            <div className="flex-1 p-2.5 rounded-lg bg-slate-50 text-center">
              <p className="text-lg font-bold text-slate-700">{payments.length}</p>
              <p className="text-[10px] text-muted-foreground">Records</p>
            </div>
          </div>
        )}

        {/* Payment History */}
        {payments.length > 0 ? (
          <div className="space-y-1.5">
            {payments.map(p => (
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 text-xs" data-testid={`rent-payment-${p.id}`}>
                <div className="flex items-center gap-2">
                  {p.status === 'paid' ? <CheckCircle size={14} className="text-emerald-600" />
                   : p.status === 'late' ? <AlertCircle size={14} className="text-red-500" />
                   : <Clock size={14} className="text-amber-500" />}
                  <div>
                    <span className="font-medium">${p.amount}</span>
                    <span className="text-muted-foreground ml-2">{p.period}</span>
                    {p.notes && <p className="text-[10px] text-muted-foreground mt-0.5">{p.notes}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{p.payment_method?.replace(/_/g, ' ')}</Badge>
                  <Badge className={
                    p.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]'
                    : p.status === 'late' ? 'bg-red-50 text-red-700 border-0 text-[10px]'
                    : p.status === 'waived' ? 'bg-blue-50 text-blue-700 border-0 text-[10px]'
                    : 'bg-amber-50 text-amber-700 border-0 text-[10px]'
                  }>{p.status}</Badge>
                  <span className="text-muted-foreground">{p.payment_date}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-3">No rent payments recorded</p>
        )}
      </CardContent>
    </Card>
  );
}
